import { useCallback, useEffect, useMemo } from "react";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Input, Form, Button, Typography } from "antd";

import {
  addVehicle,
  getDropdownUsers,
  getClients,
  setModalName,
  setModels,
  initializeZones, getPackageList, getDropdownUsersByClientID, getPlanList, getSubscriptionGroupsByClient,
} from "store/actions";
import { AppState } from "store";
import { parseClients, parseModels, parseUsers, parseZoneOptions } from "utils/parsers";
import { FormItem, SelectSearch } from "components";
import { VEHICLE_FORMIK } from "utils/formik-data";
import { VehicleValues } from "types/formik";
import { modelTypes, vehicleColors } from "../utils/mapped-data";
import { getPackageCode, getPlanCode, parsePackages, parsePlans } from "../utils/parsers/billing-parser";

const { Title, Text } = Typography;

export default function AddVehiclesForm() {
  const dispatch = useDispatch();
  const {
    vehicles: { models, clients },
    users: { dropdownUsers },
    zones: { zones },
    common: { userType = 1 },
    billing: { packages, plans, subscriptionGroups }
  } = useSelector<AppState, AppState>((state) => state);

  const modelsOptions = useMemo(() => parseModels(models), [models]);
  const usersOptions = useMemo(() => parseUsers(dropdownUsers), [dropdownUsers]);
  const clientOptions = useMemo(() => parseClients(clients), [clients]);
  const zonesOptions = useMemo(() => parseZoneOptions(zones), [zones]);
  const packagesList = useMemo(() => parsePackages(packages), [packages])
  const planList = useMemo(() => parsePlans(plans), [plans])
  const subscriptionGroupOptions = useMemo(
    () => (subscriptionGroups || []).map((g) => ({ label: g.name, value: g.name })),
    [subscriptionGroups]
  );

  const onCancel = useCallback(() => {
    dispatch(setModalName(undefined));
  }, [dispatch]);

  const onSubmit = useCallback(
    (values: VehicleValues) => {
      dispatch(
        addVehicle({
          licensePlate: values.vehicle_id,
          modelId: values.vehicle_model,
          ownerOrgId: values.client || "",
          currentlyAssignedToArrayList: values?.assign_to || [],
          locationName: values?.location || "",
          deviceIotId: values.iot_id,
          zoneId: values.zone || "",
          vin: values.vin || "",
          networkId: values.network_id || "",
          color: values.color,
          package_id: values.package_id || "",
          plan_id: values.plan_id || "",
          package_code: getPackageCode(packages, values.package_id || ""),
          plan_code: getPlanCode(plans, values.plan_id || ""),
          subscription_group_id: values.subscription_group_id || "",
        })
      );
      onCancel();
    },
    [dispatch, onCancel, packages, plans]
  );
  //Get Plan List according to the selected package
  const onPackageChange = function (value: string) {
    console.log("id : ", value)
    dispatch(getPlanList({ id: value || "" }))
  }
  // Fetch subscription groups when a client is selected
  const onClientChange = function (value: string) {
    if (value) {
      dispatch(getSubscriptionGroupsByClient({ clientId: value }))
    }
  }
  useEffect(() => {
    dispatch(setModels());
    dispatch(getClients());
    dispatch(getDropdownUsers());
    dispatch(initializeZones());
    dispatch(getPackageList())
  }, [dispatch]);

  return (
    <Formik
      initialValues={VEHICLE_FORMIK.initialValues}
      validationSchema={VEHICLE_FORMIK.validationSchema}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        isValid,
        dirty,
        handleSubmit,
        handleChange,
        setFieldValue,
      }) => (
        <div className="form-container">
          <div className="form-header">
            <Title level={5} className="form-title">
              Add Vehicle
            </Title>
          </div>
          <Text className="description" type="secondary">
            Kindly provide the information to add a vehicle
          </Text>
          <Form onFinish={handleSubmit} className="--add-vehicles-form">
            <div className="form-fields-section">
              <div className="form-field-wrapper">
                <FormItem label="Vehicle ID" required error={errors.vehicle_id}>
                  <Input
                    name="vehicle_id"
                    value={values.vehicle_id}
                    onChange={handleChange}
                    placeholder="Vehicle ID"
                  />
                </FormItem>
              </div>

              <div className="form-field-wrapper">
                <FormItem label="Vehicle VIN" required error={errors.vin}>
                  <Input
                    name="vin"
                    value={values.vin}
                    onChange={handleChange}
                    placeholder="Vehicle VIN"
                  />
                </FormItem>
              </div>

              <div className="form-field-wrapper">
                <FormItem label="Model" error={errors.vehicle_model} required>
                  <SelectSearch
                    value={values.vehicle_model}
                    items={modelsOptions}
                    setFieldValue={setFieldValue}
                    fieldName="vehicle_model"
                    placeholder="Select"
                  />
                </FormItem>
              </div>

              {/*<div className="form-field-wrapper">*/}
              {/*  <FormItem error={errors.assign_to as string} label="Assignee">*/}
              {/*    <SelectSearch*/}
              {/*      value={values.assign_to}*/}
              {/*      items={usersOptions}*/}
              {/*      setFieldValue={setFieldValue}*/}
              {/*      fieldName="assign_to"*/}
              {/*      placeholder="Select User"*/}
              {/*      isMultiple*/}
              {/*    />*/}
              {/*  </FormItem>*/}
              {/*</div>*/}

              <div className="form-field-wrapper">
                <FormItem label="Device ID" error={errors.iot_id} required>
                  <Input
                    name="iot_id"
                    value={values.iot_id}
                    onChange={handleChange}
                    placeholder="000000000000000"
                  />
                </FormItem>
              </div>

              <div className="form-field-wrapper">
                <FormItem error={errors.zone} label="Zone">
                  <SelectSearch
                    value={values.zone}
                    items={zonesOptions}
                    setFieldValue={setFieldValue}
                    fieldName="zone"
                    placeholder="Select "
                  />
                </FormItem>
              </div>

              {(userType <= 2 || userType == 7) && <div className="form-field-wrapper">
                <FormItem error={errors.client} label="Client">
                  <SelectSearch
                    value={values.client}
                    items={clientOptions}
                    setFieldValue={setFieldValue}
                    fieldName="client"
                    placeholder="Select "
                    onChangeProp={onClientChange}
                  />
                </FormItem>
              </div>}

              {(userType <= 2 || userType == 7) && values.client && (
                <div className="form-field-wrapper">
                  <FormItem label="Subscription Group" error={errors.subscription_group_id as string}>
                    <SelectSearch
                      value={values.subscription_group_id || ""}
                      items={subscriptionGroupOptions}
                      setFieldValue={setFieldValue}
                      fieldName="subscription_group_id"
                      placeholder="Select subscription group"
                    />
                  </FormItem>
                </div>
              )}

              <div className="form-field-wrapper">
                <FormItem error={errors.color} label="Color">
                  <SelectSearch
                    value={values.color || 1}
                    items={vehicleColors}
                    setFieldValue={setFieldValue}
                    fieldName="color"
                    placeholder="Select "
                  />
                </FormItem>
              </div>

              {/*<div className="form-field-wrapper">*/}
              {/*  <FormItem label="Registration" error={errors.location}>*/}
              {/*    <Input*/}
              {/*      name="location"*/}
              {/*      value={values.location}*/}
              {/*      onChange={handleChange}*/}
              {/*      placeholder="Linked to Vehicle"*/}
              {/*    />*/}
              {/*  </FormItem>*/}
              {/*</div>*/}

              <div className="form-field-wrapper">
                <FormItem label="Network ID" error={errors.network_id}>
                  <Input
                    name="network_id"
                    value={values.network_id}
                    onChange={handleChange}
                    placeholder="03001122123"
                  />
                </FormItem>
              </div>
            </div>

            {(userType <= 2 || userType == 7) && <>
              <div className="form-field-wrapper">
                <FormItem error={errors.package_id} label="Pricing Template">
                  <SelectSearch
                    value={values.package_id}
                    items={packagesList}
                    setFieldValue={setFieldValue}
                    fieldName="package_id"
                    placeholder="Select "
                    onChangeProp={onPackageChange}
                  />
                </FormItem>
              </div>
              <div className="form-field-wrapper">
                <FormItem error={errors.plan_id} label="Plan">
                  <SelectSearch
                    value={values.plan_id}
                    items={planList}
                    setFieldValue={setFieldValue}
                    fieldName="plan_id"
                    placeholder="Select "
                  />
                </FormItem>
              </div>
            </>}

            <div className="action-btn-container">
              <Button className="cancel-btn" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" disabled={!(isValid && dirty)}>
                Add
              </Button>
            </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}